<script setup>
import { reactive, computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import CustomToolsAPI from 'dashboard/api/polaris/customTools';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import ParamRow from './ParamRow.vue';
import AuthConfig from './AuthConfig.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  tool: {
    type: Object,
    default: () => ({}),
  },
});

// Phase 4.7: the same form, now inside the tool drawer (CustomToolEditorDrawer) and split into sections that open
// and close (Q1): basics, address and method, authentication, parameters, test. Save and Close are the drawer's;
// `submit()` is exposed for its Save and emits `submit` when valid. A section with an error opens by itself, so no
// error hides in a folded section. `dirty` (exposed) says whether anything changed since it opened.
const emit = defineEmits(['submit']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('polarisCustomTools/getUIFlags'),
};

const initialState = {
  title: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  request_template: '',
  response_template: '',
  auth_type: 'none',
  auth_config: {},
  param_schema: [],
};

const state = reactive({ ...initialState });
const snapshot = ref(JSON.stringify(initialState));
const dirty = computed(() => JSON.stringify(state) !== snapshot.value);

const openSections = reactive({
  basics: true,
  endpoint: true,
  auth: true,
  params: true,
  test: false,
});

// Populate form when in edit mode
watch(
  () => props.tool,
  newTool => {
    if (props.mode === 'edit' && newTool && newTool.id) {
      state.title = newTool.title || '';
      state.description = newTool.description || '';
      state.endpoint_url = newTool.endpoint_url || '';
      state.http_method = newTool.http_method || 'GET';
      state.request_template = newTool.request_template || '';
      state.response_template = newTool.response_template || '';
      state.auth_type = newTool.auth_type || 'none';
      state.auth_config = newTool.auth_config || {};
      state.param_schema = (newTool.param_schema || []).map(param => ({
        ...param,
      }));
    }
    snapshot.value = JSON.stringify(state);
  },
  { immediate: true }
);

const DEFAULT_PARAM = {
  name: '',
  type: 'string',
  description: '',
  required: false,
};

// OpenAI enforces a 64-char limit on function names. The backend slug is
// "custom_" (7 chars) + parameterized title, so cap the title conservatively.
const MAX_TOOL_NAME_LENGTH = 55;

const validationRules = {
  title: { required, maxLength: maxLength(MAX_TOOL_NAME_LENGTH) },
  endpoint_url: { required },
  http_method: { required },
  auth_type: { required },
};

const httpMethodOptions = computed(() => [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
]);

const authTypeOptions = computed(() => [
  { value: 'none', label: t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER') },
  { value: 'basic', label: t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC') },
  {
    value: 'api_key',
    label: t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  },
]);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? formState.uiFlags.value.updatingItem
    : formState.uiFlags.value.creatingItem
);

const getErrorMessage = (field, errorKey) => {
  if (!v$.value[field].$error) return '';

  const failedRule = v$.value[field].$errors[0]?.$validator;
  if (failedRule === 'maxLength') {
    return t(`POLARIS.CUSTOM_TOOLS.FORM.${errorKey}.MAX_LENGTH_ERROR`, {
      max: MAX_TOOL_NAME_LENGTH,
    });
  }
  return t(`POLARIS.CUSTOM_TOOLS.FORM.${errorKey}.ERROR`);
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  endpoint_url: getErrorMessage('endpoint_url', 'ENDPOINT_URL'),
}));

const paramsRef = useTemplateRef('paramsRef');

const isParamsValid = () => {
  if (!paramsRef.value || paramsRef.value.length === 0) {
    return true;
  }
  return paramsRef.value.every(param => param.validate());
};

const removeParam = index => {
  state.param_schema.splice(index, 1);
};

const addParam = () => {
  state.param_schema.push({ ...DEFAULT_PARAM });
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  const paramsValid = isParamsValid();
  if (!isFormValid || !paramsValid) {
    if (formErrors.value.title) openSections.basics = true;
    if (formErrors.value.endpoint_url) openSections.endpoint = true;
    if (!paramsValid) openSections.params = true;
    return;
  }

  emit('submit', state);
};

const markSaved = () => {
  snapshot.value = JSON.stringify(state);
};

defineExpose({ submit: handleSubmit, dirty, isLoading, markSaved });

const isTesting = ref(false);
const testResult = ref(null);
const isTestDisabled = computed(
  () => state.endpoint_url.includes('{{') || !!state.request_template
);

const handleTest = async () => {
  if (!state.endpoint_url) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const { data } = await CustomToolsAPI.test(state);
    const isOk = data.status >= 200 && data.status < 300;
    testResult.value = { success: isOk, status: data.status };
  } catch (e) {
    const message =
      e.response?.data?.error || t('POLARIS.CUSTOM_TOOLS.TEST.ERROR');
    testResult.value = { success: false, message };
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <form class="grid gap-6" @submit.prevent="handleSubmit">
    <FoldingGroup
      v-model:open="openSections.basics"
      :title="t('POLARIS.PAGES.TOOL_EDITOR.BASICS')"
    >
      <Input
        v-model="state.title"
        :label="t('POLARIS.CUSTOM_TOOLS.FORM.TITLE.LABEL')"
        :placeholder="t('POLARIS.CUSTOM_TOOLS.FORM.TITLE.PLACEHOLDER')"
        :message="formErrors.title"
        :message-type="formErrors.title ? 'error' : 'info'"
      />
      <TextArea
        v-model="state.description"
        :label="t('POLARIS.CUSTOM_TOOLS.FORM.DESCRIPTION.LABEL')"
        :placeholder="t('POLARIS.CUSTOM_TOOLS.FORM.DESCRIPTION.PLACEHOLDER')"
        :rows="2"
      />
    </FoldingGroup>

    <FoldingGroup
      v-model:open="openSections.endpoint"
      :title="t('POLARIS.PAGES.TOOL_EDITOR.ENDPOINT')"
    >
      <div class="flex gap-2">
        <div class="flex w-28 flex-col gap-1">
          <label class="mb-0.5 type-polaris-label text-polaris-foreground">
            {{ t('POLARIS.CUSTOM_TOOLS.FORM.HTTP_METHOD.LABEL') }}
          </label>
          <ComboBox
            v-model="state.http_method"
            :options="httpMethodOptions"
            class="[&>div>button]:bg-n-alpha-black2 [&_li]:font-mono [&_button]:font-mono [&>div>button]:outline-offset-[-1px]"
          />
        </div>
        <Input
          v-model="state.endpoint_url"
          :label="t('POLARIS.CUSTOM_TOOLS.FORM.ENDPOINT_URL.LABEL')"
          :placeholder="t('POLARIS.CUSTOM_TOOLS.FORM.ENDPOINT_URL.PLACEHOLDER')"
          :message="formErrors.endpoint_url"
          :message-type="formErrors.endpoint_url ? 'error' : 'info'"
          class="min-w-0 flex-1"
        />
      </div>
      <TextArea
        v-if="state.http_method === 'POST'"
        v-model="state.request_template"
        :label="t('POLARIS.CUSTOM_TOOLS.FORM.REQUEST_TEMPLATE.LABEL')"
        :placeholder="
          t('POLARIS.CUSTOM_TOOLS.FORM.REQUEST_TEMPLATE.PLACEHOLDER')
        "
        :rows="4"
        class="[&_textarea]:font-mono"
      />
      <TextArea
        v-model="state.response_template"
        :label="t('POLARIS.CUSTOM_TOOLS.FORM.RESPONSE_TEMPLATE.LABEL')"
        :placeholder="
          t('POLARIS.CUSTOM_TOOLS.FORM.RESPONSE_TEMPLATE.PLACEHOLDER')
        "
        :rows="4"
        class="[&_textarea]:font-mono"
      />
    </FoldingGroup>

    <FoldingGroup
      v-model:open="openSections.auth"
      :title="t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPE.LABEL')"
    >
      <ComboBox
        v-model="state.auth_type"
        :options="authTypeOptions"
        :aria-label="t('POLARIS.CUSTOM_TOOLS.FORM.AUTH_TYPE.LABEL')"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <AuthConfig
        v-model:auth-config="state.auth_config"
        :auth-type="state.auth_type"
      />
    </FoldingGroup>

    <FoldingGroup
      v-model:open="openSections.params"
      :title="t('POLARIS.CUSTOM_TOOLS.FORM.PARAMETERS.LABEL')"
      :description="t('POLARIS.CUSTOM_TOOLS.FORM.PARAMETERS.HELP_TEXT')"
      :count="state.param_schema.length"
    >
      <ul
        v-if="state.param_schema.length > 0"
        class="m-0 grid list-none gap-2 p-0"
      >
        <ParamRow
          v-for="(param, index) in state.param_schema"
          :key="index"
          ref="paramsRef"
          v-model:name="param.name"
          v-model:type="param.type"
          v-model:description="param.description"
          v-model:required="param.required"
          @remove="removeParam(index)"
        />
      </ul>
      <template #actions>
        <Button
          type="button"
          sm
          ghost
          blue
          icon="i-lucide-plus"
          :label="t('POLARIS.CUSTOM_TOOLS.FORM.ADD_PARAMETER')"
          @click="addParam"
        />
      </template>
    </FoldingGroup>

    <FoldingGroup
      v-model:open="openSections.test"
      :title="t('POLARIS.PAGES.TOOL_EDITOR.TEST')"
    >
      <p
        v-if="isTestDisabled"
        class="type-polaris-caption text-polaris-muted-foreground"
      >
        {{ t('POLARIS.CUSTOM_TOOLS.TEST.DISABLED_HINT') }}
      </p>
      <p
        v-if="testResult"
        class="flex items-center gap-2 rounded-polaris-lg px-3 py-2 type-polaris-caption"
        :class="
          testResult.success
            ? 'bg-polaris-success text-polaris-success-foreground'
            : 'bg-polaris-destructive/10 text-polaris-destructive'
        "
        role="status"
      >
        <span
          :class="
            testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
          "
          class="size-3.5 shrink-0"
        />
        {{
          testResult.status
            ? t('POLARIS.CUSTOM_TOOLS.TEST.SUCCESS', {
                status: testResult.status,
              })
            : testResult.message
        }}
      </p>
      <template #actions>
        <Button
          type="button"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-play"
          :label="t('POLARIS.CUSTOM_TOOLS.TEST.BUTTON')"
          :is-loading="isTesting"
          :disabled="isTesting || !state.endpoint_url || isTestDisabled"
          @click="handleTest"
        />
      </template>
    </FoldingGroup>
  </form>
</template>
